import { useTicketCommercialCloseout } from '@/hooks/tickets/useTicketCommercialCloseout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lock, FileWarning } from 'lucide-react';

interface Props {
  ticketId: string;
}

function fmt(n: number | null | undefined) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(n));
}

/**
 * Phase 16 — Sealed commercial closeout snapshot.
 *
 * This view shows the latest version. Closeouts are immutable; corrections
 * appear as higher version numbers superseding the prior row.
 */
export function TicketCloseoutPanel({ ticketId }: Props) {
  const { data, isLoading } = useTicketCommercialCloseout(ticketId);

  if (isLoading) return <div className="text-sm text-muted-foreground p-4">Loading closeout…</div>;
  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileWarning className="h-4 w-4 text-muted-foreground" />
            Not yet closed out
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A commercial closeout snapshot will be sealed when this ticket reaches the closed state.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" />
          Commercial closeout
          <Badge variant="outline" className="ml-2">v{data.version}</Badge>
          {data.supersedes_id && (
            <Badge variant="secondary" className="text-[10px]">supersedes prior version</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-3">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
          <dt className="text-muted-foreground">Estimated</dt>
          <dd className="text-right font-medium">{fmt(data.estimated_total)}</dd>
          <dt className="text-muted-foreground">Approved</dt>
          <dd className="text-right font-medium">{fmt(data.approved_total)}</dd>
          <dt className="text-muted-foreground">Performed</dt>
          <dd className="text-right font-medium">{fmt(data.performed_total)}</dd>
          <dt className="text-muted-foreground">Sold</dt>
          <dd className="text-right font-medium">{fmt(data.sold_total)}</dd>
          <dt className="text-muted-foreground">Invoiced</dt>
          <dd className="text-right font-medium">{fmt(data.invoiced_total)}</dd>
          <dt className="text-muted-foreground">Paid</dt>
          <dd className="text-right font-medium">{fmt(data.paid_total)}</dd>
          <dt className="text-muted-foreground">Refunded</dt>
          <dd className="text-right font-medium">{fmt(data.refunded_total)}</dd>
          <dt className="text-muted-foreground">Gross profit</dt>
          <dd className="text-right font-medium">
            {fmt(data.gp_dollars)}
            {data.gp_percent !== null && (
              <span className="text-muted-foreground"> ({data.gp_percent}%)</span>
            )}
          </dd>
        </dl>
        {data.variance_reason && (
          <div className="rounded-md border border-warning/40 bg-warning/5 p-2 text-xs">
            <span className="font-medium">Variance reason: </span>
            {data.variance_reason}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground pt-1 border-t">
          Sealed {new Date(data.closed_at).toLocaleString()} · hash {data.closeout_hash.slice(0, 8)}…
        </div>
      </CardContent>
    </Card>
  );
}
